import { useCallback, useMemo, useState } from 'react'
import { GoogleAuthProvider, EmailAuthProvider, getAuth } from 'firebase/auth'
import * as firebaseui from 'firebaseui'

import FirebaseAuthWidget from '../../components/firebase-auth-widget/FirebaseAuthWidget'
import { enableDevAuthBypass } from '../../_data/dev-auth-bypass'
import {
  PageContainer,
  ContentCard,
  Wordmark,
  Heading,
  Subheading,
  ErrorMessage,
  WidgetContainer,
  DevBypassDivider,
  DevBypassButton,
  DevBypassHint,
} from './styled'

/**
 * Sign-in page. Renders the FirebaseUI widget which handles Google,
 * email + password, AND email-link sign-in in a single component.
 * Sign-up is the same flow — FirebaseUI auto-routes new emails into
 * account creation. Post-sign-in navigation is handled by RouteGuard +
 * AuthProvider — `signInSuccessWithAuthResult` returns false so the widget
 * does not auto-redirect; instead `onAuthStateChanged` fires inside
 * AuthProvider, which flips `isAuthenticated` and the public-only guard
 * pushes the user to `/dashboard`.
 *
 * @returns {JSX.Element}
 */
const SignInPage = (): JSX.Element => {
  const firebaseAuth = useMemo(() => getAuth(), [])
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleSignInFailure = useCallback(
    (failure: firebaseui.auth.AuthUIError): Promise<void> => {
      console.error(failure)
      setErrorMessage(failure.message ?? 'Sign-in failed — try again in a sec.')
      return Promise.resolve()
    },
    [],
  )

  const firebaseLoginOptions = useMemo<firebaseui.auth.Config>(
    () => ({
      signInFlow: 'popup',
      siteName: 'QuickPass',
      callbacks: {
        signInSuccessWithAuthResult: () => false,
        signInFailure: handleSignInFailure,
      },
      signInOptions: [
        {
          provider: GoogleAuthProvider.PROVIDER_ID,
          fullLabel: 'Continue with Google',
        },
        {
          provider: EmailAuthProvider.PROVIDER_ID,
          fullLabel: 'Continue with email',
          requireDisplayName: false,
        },
      ],
    }),
    [handleSignInFailure],
  )

  return (
    <PageContainer>
      <ContentCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.5 } }}
      >
        <Wordmark>QuickPass</Wordmark>
        <Heading>Welcome</Heading>
        <Subheading>
          Sign in or create your QuickPass — same flow either way.
        </Subheading>

        <WidgetContainer>
          <FirebaseAuthWidget
            firebaseAuth={firebaseAuth}
            uiConfig={firebaseLoginOptions}
          />
        </WidgetContainer>

        {errorMessage !== '' ? (
          <ErrorMessage
            initial={{ opacity: 0, x: -8 }}
            animate={{
              opacity: 1,
              x: [0, -4, 4, -4, 4, 0],
              transition: { duration: 0.4 },
            }}
          >
            {errorMessage}
          </ErrorMessage>
        ) : null}

        {import.meta.env.DEV === true ? (
          <>
            <DevBypassDivider>Dev only</DevBypassDivider>
            <DevBypassButton type="button" onClick={enableDevAuthBypass}>
              Skip auth — load synthetic renter
            </DevBypassButton>
            <DevBypassHint>
              Visible because this is a dev build. Stripped from production.
            </DevBypassHint>
          </>
        ) : null}
      </ContentCard>
    </PageContainer>
  )
}

export default SignInPage
